import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Collapse, Grid, IconButton, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { ExpandLess, ExpandMore, InfoOutlined } from '@mui/icons-material';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, OrderPageTypes, PassOrderPageData, RedeemableOnItemInput } from '../../../api';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { RedeemableOnMultiSelect } from './RedeemableOnMultiSelect';
import { OrderPagesMultiSelect } from '../../../userSettings/OrderPagesMultiSelect';
import { SectionSaveRegistrar } from '../../../experienceSettings/saveHeader/SectionSaveRegistrar';
import type { ChangeEntry } from '../../../experienceSettings/saveHeader/SaveHeaderContext';
import { collect, diffIdList } from '../../../experienceSettings/saveHeader/diffBuilders';
import { getOrderPages } from '../../../utils/AccountUtils';
import { useLanguage } from '../../../common/GeneralUtils';
import { PassSettingsPageTabs } from '../PassSettingsPageTabs';

type Props = {
  passOrderPage: PassOrderPageData;
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
};

type FormValues = {
  redeemable_on: RedeemableOnItemInput[];
  parallel_activity_order_page_ids: string[];
};

export const FixedScheduleSettingsSection = ({ passOrderPage, account, updateLocalAccount }: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();

  const totalOrderPages = getOrderPages(account).length;
  const collapsible = totalOrderPages > 4;
  const [redeemableExpanded, setRedeemableExpanded] = useState(!collapsible);
  const [parallelExpanded, setParallelExpanded] = useState(!collapsible);

  const initialValues: FormValues = {
    redeemable_on: passOrderPage.redeemable_on || [],
    parallel_activity_order_page_ids: passOrderPage.parallel_activity_order_page_ids || [],
  };

  const handleSubmit = async (values: FormValues, { setStatus }: FormikHelpers<FormValues>) => {
    try {
      posthog.capture('Editing pass settings', { section: 'settingsSection', passOrderPageId: passOrderPage.id });
      const updatedAccount = await AccountService.updatePassOrderPage({
        id: passOrderPage.id,
        redeemable_on: values.redeemable_on,
        parallel_activity_order_page_ids: values.parallel_activity_order_page_ids,
      });
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const orderPageLabel = (id: string) => {
    const op = getOrderPages(account, true).find((o) => o.id === id);
    return op?.label?.[language] || id;
  };

  const getChanges = (values: FormValues, iv: FormValues): ChangeEntry[] =>
    collect(
      diffIdList(
        t('passes.redeemable_on'),
        iv.redeemable_on?.map((r) => r.order_page_id),
        values.redeemable_on?.map((r) => r.order_page_id),
        orderPageLabel,
      ),
      diffIdList(
        t('passes.parallel_activity_pages'),
        iv.parallel_activity_order_page_ids,
        values.parallel_activity_order_page_ids,
        orderPageLabel,
      ),
    );

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      {(formik) => {
        const { values, isSubmitting, errors, touched, handleChange } = formik;
        const formIsDisabled = readOnly || isSubmitting;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar tabKey={PassSettingsPageTabs.SETTINGS} getChanges={getChanges} disabled={readOnly} />
            <Grid container justifyContent="space-between" flexWrap="nowrap" alignItems="flex-start">
              <Grid container flexDirection="column" gap={2}>
                <Grid container flexDirection="column">
                  <Grid container alignItems="center" gap={0.5} flexWrap="nowrap">
                    {collapsible && (
                      <IconButton
                        size="small"
                        onClick={() => setRedeemableExpanded((v) => !v)}
                        aria-label={
                          redeemableExpanded
                            ? t('auditlog.change_details.collapse')
                            : t('auditlog.change_details.expand')
                        }>
                        {redeemableExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                    <Typography fontWeight="700">{t('passes.redeemable_on')}</Typography>
                    <Tooltip title={t('passes.slot_capacity_note')} placement="top">
                      <InfoOutlined fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                    {collapsible && (
                      <Typography variant="body2" color="text.secondary" sx={{ marginInlineStart: 1 }}>
                        {t('passes.selected_of_total', {
                          count: values.redeemable_on.length,
                          total: totalOrderPages,
                        })}
                      </Typography>
                    )}
                  </Grid>
                  <Collapse in={!collapsible || redeemableExpanded} unmountOnExit>
                    <Grid container flexDirection="column" gap={2} mt={2}>
                      <Typography variant="body2" color="text.secondary">
                        {t('passes.slot_capacity_note')}
                      </Typography>
                      <Field name="redeemable_on">
                        {({ form }: FieldProps<RedeemableOnItemInput[]>) => (
                          <RedeemableOnMultiSelect
                            value={values.redeemable_on}
                            onChange={(items) => form.setFieldValue('redeemable_on', items)}
                            account={account}
                            disabled={formIsDisabled}
                            orderPageTypeFilter={[OrderPageTypes.EVENT]}
                          />
                        )}
                      </Field>
                    </Grid>
                  </Collapse>
                  {values.redeemable_on.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {t('passes.redeemable_on_warning')}
                    </Alert>
                  )}
                </Grid>

                <Grid container flexDirection="column" mt={2}>
                  <Grid container alignItems="center" gap={0.5} flexWrap="nowrap">
                    {collapsible && (
                      <IconButton
                        size="small"
                        onClick={() => setParallelExpanded((v) => !v)}
                        aria-label={
                          parallelExpanded ? t('auditlog.change_details.collapse') : t('auditlog.change_details.expand')
                        }>
                        {parallelExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                    <Typography fontWeight="700">{t('passes.parallel_activity_pages')}</Typography>
                    {collapsible && (
                      <Typography variant="body2" color="text.secondary" sx={{ marginInlineStart: 1 }}>
                        {t('passes.selected_of_total', {
                          count: values.parallel_activity_order_page_ids.length,
                          total: totalOrderPages,
                        })}
                      </Typography>
                    )}
                  </Grid>
                  <Collapse in={!collapsible || parallelExpanded} unmountOnExit>
                    <Grid container flexDirection="column" gap={2} mt={2}>
                      <Typography variant="body2" color="text.secondary">
                        {t('passes.parallel_activity_pages_description')}
                      </Typography>
                      <OrderPagesMultiSelect
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        orderPagesFieldName="parallel_activity_order_page_ids"
                        account={account}
                      />
                    </Grid>
                  </Collapse>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
